import { Menu, Theme } from '@mui/material';
import { WbGridCol, WbHeading, WbText } from '@workbench/react';
import { memo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import BeamButton from '../common/components/input/BeamButton';
import LoginButton from '../common/components/input/LoginButton';
import LogoutButton from '../common/components/input/LogoutButton';
import { useTranslation } from '../common/i18n/useTranslation';
import { endpointConfig } from '../configuration/configuration';
import { isDevOrLocalEnv } from '../configuration/confUtils';
import { Routes } from '../routes/routes';
import { useNavigation } from '../utils/customHooks';
import { LicenseModel } from '../utils/enums';
import { useClientCompanyRegistrationContext } from './client/companyRegistration/context/ClientCompanyRegistrationContext';

const useStyles = makeStyles()((theme: Theme) => ({
    divContainer: {
        width: theme.spacing(50),
        padding: theme.spacing(2),
    },
    userButton: {
        display: 'inline-flex',
        width: '100%',
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    spacingContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    fullWidth: {
        display: 'inline-flex',
        width: '100%',
    },
    userActionButtons: {
        marginBottom: theme.spacing(1),
    },
}));

interface LoginMenuProps {
    anchorElement: null | HTMLElement;
    setAnchorElement: (element: null | HTMLElement) => void;
    handleOpenChangeDevUserDialog: () => void;
}

function LoginMenu(props: Readonly<LoginMenuProps>) {
    const { classes } = useStyles();
    const translate = useTranslation();
    const open = Boolean(props.anchorElement);
    const { registration, isFullBeam, setIsUmasRegistrationModalOpen, userInfo, isAdmin, isAdminAndUser } =
        useClientCompanyRegistrationContext();
    const navigate = useNavigation();
    const defaultNavigate = useNavigate();
    const isAdminPage = useMatch(`/${Routes.admin}/*`);

    const handleClose = () => {
        props.setAnchorElement(null);
    };

    const changeUserButton = (
        <BeamButton
            variant="secondary"
            className={classes.userButton}
            onClick={() => {
                handleClose();
                props.handleOpenChangeDevUserDialog();
            }}
        >
            {translate('edit_user')}
        </BeamButton>
    );

    const isUserActionsVisable = () => {
        return isDevOrLocalEnv() || endpointConfig.env.NAME === 'INT' || !isAdmin || isAdminAndUser;
    };

    const renderGenInfo = () => {
        return (
            <>
                <WbHeading size="m">
                    {translate('common_logged_in_as', {
                        name: `${userInfo?.givenName} ${userInfo?.familyName}`,
                    })}
                </WbHeading>
                <WbText size="m">
                    {translate('common_logged_in_as_userid', {
                        userId: userInfo?.userId,
                    })}
                </WbText>
                <WbText size="m">
                    {translate('license_model', {
                        licenseModel: isFullBeam() ? LicenseModel.BEAM_FULL : LicenseModel.BEAM_FREE,
                    })}
                </WbText>
            </>
        );
    };

    const renderDefaultStatus = () => (
        <div className={classes.spacingContainer}>
            <WbHeading size="l">{translate('common_login_header')}</WbHeading>
            {!isAdminPage ? (
                <WbText size="s">{translate('common_login_text')}</WbText>
            ) : (
                <>
                    <WbText size="s">{translate('login_only_homepage')}</WbText>
                    <BeamButton
                        className={classes.fullWidth}
                        variant="primary"
                        onClick={async () => {
                            handleClose();
                            await defaultNavigate('/');
                        }}
                    >
                        {translate('homepage')}
                    </BeamButton>
                </>
            )}
            {!isAdminPage && <LoginButton variant="primary" isLoginMenu />}
            <BeamButton
                className={classes.fullWidth}
                variant="secondary"
                onClick={() => {
                    handleClose();
                    setIsUmasRegistrationModalOpen(true);
                }}
            >
                {translate('registration')}
            </BeamButton>
        </div>
    );

    const renderBeamLightStatus = () => (
        <>
            {renderGenInfo()}
            {endpointConfig.env.NAME === 'DEV' || endpointConfig.env.NAME === 'LOCAL' ? changeUserButton : null}
            <LogoutButton className={classes.fullWidth} variant="secondary" />
        </>
    );

    const renderBeamFullAccessStatus = () => (
        <>
            {renderGenInfo()}
            {isUserActionsVisable() && (
                <WbGridCol className={classes.userActionButtons}>
                    <BeamButton
                        variant="tertiary"
                        onClick={async () => {
                            handleClose();
                            await navigate(Routes.dashboard_my_company);
                        }}
                    >
                        {translate('my_company')}
                    </BeamButton>

                    {isAdminAndUser && (
                        <BeamButton
                            variant="tertiary"
                            onClick={async () => {
                                handleClose();
                                await navigate(isAdminPage ? Routes.home : Routes.admin);
                            }}
                        >
                            {isAdminPage ? translate('beam_Manufacturer') : translate('admin_area')}
                        </BeamButton>
                    )}
                </WbGridCol>
            )}
            {isDevOrLocalEnv() ? changeUserButton : null}
            <LogoutButton className={classes.fullWidth} variant="secondary" />
        </>
    );

    const renderLoginMenuContent = () => {
        if (userInfo?.userId) {
            if (registration?.termsOfUseApproved) {
                return renderBeamFullAccessStatus();
            } else {
                return renderBeamLightStatus();
            }
        }

        return renderDefaultStatus();
    };

    return (
        <Menu
            id="basic-menu"
            anchorEl={props.anchorElement}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': 'basic-button',
            }}
            elevation={1}
        >
            <div className={classes.divContainer}>{renderLoginMenuContent()}</div>
        </Menu>
    );
}

export default memo(LoginMenu);
